import { DashboardService, IDocument, DateRange, DocumentPeriods, IDocumentPeriod, IDetailDocument, noDocument } from '../dashboard.service';
import { Component, OnInit, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { AppService } from '../../shared/app.service';
import { MenuService } from '../../shared/menu.service';
import { Subscription, of, merge, combineLatest, Subject, Observable } from 'rxjs';
import { state2 } from '../../shared/state';
import { jumpToAnchor } from '../../shared/helpers';
import { createCmdFactory, arrayReplace } from '@cevi/rxjscommands';
import { switchMap, map, withLatestFrom, distinctUntilChanged, scan, mergeMap } from 'rxjs/operators';
import { DocumentCalloutComponent } from '../document-callout/document-callout.component';
import { NgIf, NgFor, NgClass, AsyncPipe, DatePipe } from '@angular/common';

interface IDocumentenState {
  documentperiod: IDocumentPeriod;
  documentfragment?: string;
}

interface IDocumentsVm {
  documenten: IDocument[];
  documentPeriods: IDocumentPeriod[];
  currentPeriod: IDocumentPeriod;
  viewState: IDocumentenState;
}

const emptyVm: IDocumentsVm = {
  documenten: [],
  documentPeriods: DocumentPeriods,
  currentPeriod: null,
  viewState: null
};


@Component({
    selector: 'app-documenten',
    templateUrl: './documenten.component.html',
    styleUrls: ['./documenten.component.scss'],
    standalone: true,
    imports: [NgIf, NgFor, NgClass, DocumentCalloutComponent, AsyncPipe, DatePipe],
    providers:[DashboardService]
})
export class DocumentenComponent implements OnInit /*, OnDestroy */ {
  @ViewChild('documentenDiv', { static: true }) documentenDiv: ElementRef;

  vm$: Observable<IDocumentsVm>;
  vm: IDocumentsVm;

  setActivePeriodSubj = new Subject<IDocumentPeriod>();
  showDocumentSubj = new Subject<IDocument>();

  constructor(private dashboardService: DashboardService,
    private appService: AppService,
    private menuService: MenuService) {

    const cmdFactory = createCmdFactory<IDocumentsVm>();

    cmdFactory.addMapAction(this.viewStateAction$, 'viewState');
    cmdFactory.addMapAction(this.setActivePeriodAction$, 'setActivePeriod');
    cmdFactory.addMapAction(this.getDocumentsAction$, 'getDocuments');
    cmdFactory.addMapAction(this.showDocumentAction$, 'showDocument');
    this.vm$ = cmdFactory.merge(emptyVm);

  }

  private viewState$ = state2.getValueOrDefault<IDocumentenState>('documentstate', of({ documentperiod: DocumentPeriods[0] }));
  private currentPeriode$ = merge(this.viewState$.pipe(map(v => v.documentperiod)), this.setActivePeriodSubj);

  private viewStateAction$ = combineLatest([this.viewState$, this.currentPeriode$]).pipe(
    map(([viewState, currentPeriod]) => (vm: IDocumentsVm) => ({ ...vm, viewState, currentPeriod }))
  );

  private refreshDocuments$ = merge(
    of(false), // initeel
    this.appService.refresh$, // refresh page button
    this.setActivePeriodSubj.pipe(map(_ => true)) // select active period
  );

  private getDocumentsAction$ = this.refreshDocuments$.pipe(
    withLatestFrom(this.currentPeriode$),
    switchMap(([force, docPeriode]) => this.dashboardService.getAllRecenteDocumenten(docPeriode.DateRange, force)),
    map(documenten => documenten.map(d => ({
      ...d,
      DatumUpdate: new Date(d.DatumUpdate as string),
      identifier: `d_${d.Id}`
    })
    )),
    map(documenten => (vm: IDocumentsVm) => ({ ...vm, documenten }))
    //   jumpToAnchor(this.viewState.documentfragment);
  );

  private setActivePeriodAction$ = this.setActivePeriodSubj.pipe(
    distinctUntilChanged(),
    map(currentPeriod => (vm: IDocumentsVm) => ({
      ...vm,
      currentPeriod,
      documenten: [],
      viewState: { ...vm.viewState, period: currentPeriod }
    })
    )
  );

  ngOnInit() { }

  getDocumentId = (_: number, document: IDocument) => document.Id;

  private currentDocument$ = this.showDocumentSubj.pipe(
    scan((lastActiveDocument, newActiveDocument) => lastActiveDocument.Id === newActiveDocument.Id
      ? noDocument as IDocument
      : newActiveDocument)
  );

  private showDocumentAction$ = this.currentDocument$.pipe(
    switchMap(document => this.dashboardService.getDetailDocument(document)),
    withLatestFrom(this.showDocumentSubj),
    map(([detailDocument, currentDocument]) => ({ detailDocument, currentDocument })),
    map(data => (vm: IDocumentsVm) => {
      const index = vm.documenten.findIndex(d => d === data.currentDocument);
      const newDocument = { ...data.currentDocument, detail: data.detailDocument };
      return {
        ...vm,
        viewState: { ...vm.viewState, documentfragment: data.currentDocument.identifier },
        documenten: arrayReplace(vm.documenten, index, newDocument)
      };
    })
  );

  openDocument(document: IDocument) {
    this.menuService.openDocument(document.AgendapuntId ? 'agendapunt' : 'zitting', document.Id, document.Titel, document.Verwijzing);
    return false;
  }

  public showDocument=(document:IDocument)=>{
    this.showDocumentSubj.next(document);
    return false;
  }

}
